import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CheckCircle, GraduationCap } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function ConsultantFormPage() {
  const { token } = useParams();
  const [formConfig, setFormConfig] = useState(null);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [leadUid, setLeadUid] = useState('');
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ student_name: '', email: '', phone: '+91 ', course_id: '' });
  const [phoneError, setPhoneError] = useState('');

  const handlePhoneChange = (val) => {
    if (!val.startsWith('+91 ')) val = '+91 ' + val.replace(/^\+91\s?/, '');
    const digits = val.replace(/^\+91\s?/, '').replace(/\D/g, '').slice(0, 10);
    setForm(p => ({ ...p, phone: '+91 ' + digits }));
    if (digits.length > 0 && digits.length !== 10) setPhoneError('Must be 10 digits');
    else setPhoneError('');
  };

  useEffect(() => {
    axios.get(`${API_BASE}/api/consultant-form/${token}`)
      .then(r => setFormConfig(r.data))
      .catch(() => setError('This form link is invalid or inactive.'));
  }, [token]);

  const handleSubmit = async () => {
    if (!form.student_name.trim() || form.phone.replace(/^\+91\s?/, '').length !== 10) {
      alert('Student name and a valid 10-digit phone number are required.');
      return;
    }
    setLoading(true);
    try {
      const r = await axios.post(`${API_BASE}/api/consultant-form/${token}`, form);
      setLeadUid(r.data.lead_uid);
      setSubmitted(true);
    } catch (err) {
      alert(err.response?.data?.detail || 'Submission failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="w-full max-w-md border-red-200">
          <CardContent className="py-12 text-center">
            <p className="text-red-500 font-medium">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!formConfig) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <p className="text-slate-400">Loading...</p>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="w-full max-w-md border-green-200">
          <CardContent className="py-12 text-center space-y-4">
            <CheckCircle size={48} className="mx-auto text-green-500" />
            <h2 className="text-xl font-bold text-slate-900">Lead Submitted!</h2>
            <p className="text-sm text-slate-500">Reference ID: <span className="font-mono font-semibold text-slate-900">{leadUid}</span></p>
            <p className="text-xs text-slate-400">Thank you. The admissions team will be in touch shortly.</p>
            <Button variant="outline" onClick={() => { setSubmitted(false); setForm({ student_name: '', email: '', phone: '+91 ', course_id: '' }); }} data-testid="submit-another-btn">Submit Another Lead</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4" data-testid="consultant-form-page">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center border-b pb-4">
          <div className="flex items-center justify-center gap-2 mb-2">
            <GraduationCap size={24} className="text-slate-700" />
            <span className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>MBS College</span>
          </div>
          <CardTitle className="text-base font-semibold text-slate-700">Student Referral Form</CardTitle>
          <p className="text-xs text-slate-400 mt-1">Referred by: <span className="font-medium text-slate-600">{formConfig.consultant_name}</span></p>
        </CardHeader>
        <CardContent className="pt-6 space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-500 uppercase">Student's Name *</Label>
            <Input value={form.student_name} onChange={e => setForm(p => ({...p, student_name: e.target.value}))} placeholder="Full name" data-testid="cf-student-name" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-500 uppercase">Email ID</Label>
            <Input type="email" value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))} placeholder="student@email.com" data-testid="cf-email" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-500 uppercase">Phone No *</Label>
            <Input value={form.phone} onChange={e => handlePhoneChange(e.target.value)} placeholder="+91 9876543210" data-testid="cf-phone" maxLength={14} />
            {phoneError && <p className="text-xs text-red-500">{phoneError}</p>}
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-500 uppercase">Primary Course Preference</Label>
            <Select value={form.course_id || 'none'} onValueChange={v => setForm(p => ({...p, course_id: v === 'none' ? '' : v}))}>
              <SelectTrigger data-testid="cf-course"><SelectValue placeholder="Select Course" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Select —</SelectItem>
                {formConfig.courses.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={handleSubmit} disabled={loading} className="w-full bg-slate-900 hover:bg-slate-800 mt-2" data-testid="cf-submit-btn">
            {loading ? 'Submitting...' : 'Submit Lead'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
